import { useMemo } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { usePermissionStore } from '@/stores/permissionStore'
import {
  type PermissionId,
  hasCodenamePermission,
  hasPermissionId,
  hasAnyPermissionId,
} from '@/lib/permissions'

/** Check if the current user has a single permission ID. Admins always return true. */
export function usePermission(permissionId: PermissionId): boolean {
  const user = useAuthStore((s) => s.user)
  const storePermissions = usePermissionStore((s) => s.allPermissionList)
  const permissions = storePermissions ?? user?.permissions ?? []

  return useMemo(
    () => hasPermissionId(permissions, permissionId, Boolean(user?.is_company_admin)),
    [permissions, permissionId, user?.is_company_admin],
  )
}

/**
 * Check route/sidebar access by permission codename(s).
 * Codenames are resolved to numeric IDs via the map loaded at login.
 */
export function usePermissions(codenames: string[], requireAll = false): boolean {
  const user = useAuthStore((s) => s.user)
  const storePermissions = usePermissionStore((s) => s.allPermissionList)
  const codenameToId = usePermissionStore((s) => s.codenameToId)
  const permissions = storePermissions ?? user?.permissions ?? []

  return useMemo(
    () =>
      hasCodenamePermission(
        codenames,
        permissions,
        codenameToId,
        Boolean(user?.is_company_admin),
        requireAll,
      ),
    [user, codenames, permissions, codenameToId, requireAll],
  )
}

export function usePermissionIds() {
  const user = useAuthStore((s) => s.user)
  const storePermissions = usePermissionStore((s) => s.allPermissionList)
  const codenameToId = usePermissionStore((s) => s.codenameToId)
  const permissions = storePermissions ?? user?.permissions ?? []
  const isAdmin = Boolean(user?.is_company_admin)

  return useMemo(
    () => ({
      permissions,
      codenameToId,
      isAdmin,
      hasPermission: (id: PermissionId) => hasPermissionId(permissions, id, isAdmin),
      hasAnyPermission: (ids: PermissionId[]) =>
        hasAnyPermissionId(permissions, ids, isAdmin),
      hasCodename: (codenames: string | string[], requireAll = false) =>
        hasCodenamePermission(codenames, permissions, codenameToId, isAdmin, requireAll),
    }),
    [permissions, codenameToId, isAdmin],
  )
}
